import { KText } from '@/ui-library/components';
import {
  AssociatedRequisition,
  DepartmentName,
  Jobname,
  NameCell,
  OfferCreatedBy,
  OfferRevision,
  OfferStatus,
  OfficeName,
  TimeToCloseOffer,
  TimeToOffer,
} from '../cells/OfferDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const useOfferDrilldownConfig = ({ drilldownHeading = 'Offers' }) => {
  const getColumnConfig = () => {
    const columnsMap = {
      applicantName: {
        title: 'Applicant name',
        columnConfig: false,
        Header: renderDrilldownHeader('Applicant name'),
        minWidth: 200,
        accessor: 'candidate_name',
        sort: true,
        Cell: ({ row }) => NameCell(row),
      },
      timeToOffer: {
        title: 'Time to offer',
        Header: renderDrilldownHeader('Time to offer'),
        minWidth: 200,
        accessor: 'time_to_offer',
        sort: true,
        Cell: ({ row }) => TimeToOffer(row),
      },
      offerStatus: {
        title: 'Offer acceptance status',
        Header: renderDrilldownHeader('Offer acceptance status'),
        minWidth: 250,
        accessor: 'offer_status',
        sort: true,
        Cell: ({ row }) => OfferStatus(row),
      },
      associatedRequisition: {
        title: 'Associated requisition (Req ID & Role name)',
        Header: renderDrilldownHeader('Associated requisition (Req ID & Role name)'),
        minWidth: 220,
        accessor: 'requisition_code',
        sort: true,
        Cell: ({ row }) => AssociatedRequisition(row),
      },
      offerCreatedBy: {
        title: 'Offer created by',
        Header: renderDrilldownHeader('Offer created by'),
        minWidth: 180,
        accessor: 'offer_created_by',
        sort: true,
        Cell: ({ row }) => OfferCreatedBy(row),
      },
      jobName: {
        title: 'Job name',
        Header: renderDrilldownHeader('Job name'),
        minWidth: 200,
        accessor: 'job_name',
        sort: true,
        Cell: ({ row }) => Jobname(row),
      },
      department: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        minWidth: 200,
        accessor: 'department_name',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      office: {
        title: 'Office',
        Header: renderDrilldownHeader('Office'),
        minWidth: 200,
        accessor: 'office_name',
        sort: true,
        Cell: ({ row }) => OfficeName(row),
      },
      offerRevision: {
        title: 'Offer revisions',
        Header: renderDrilldownHeader('Offer revisions'),
        minWidth: 160,
        id: 'revisions_on_offer',
        accessor: 'revisions_on_data',
        sort: true,
        Cell: ({ row }) => OfferRevision(row),
      },
      timeToCloseOffer: {
        title: 'Time to close offer',
        Header: renderDrilldownHeader('Time to close offer'),
        minWidth: 200,
        accessor: 'time_to_close_offer',
        sort: true,
        Cell: ({ row }) => TimeToCloseOffer(row),
      },
    };

    const commonRequisitionCreatedByJobDeptOffice = [
      columnsMap.associatedRequisition,
      columnsMap.offerCreatedBy,
      columnsMap.jobName,
      columnsMap.department,
      columnsMap.office,
    ];

    switch (drilldownHeading) {
      case 'Offers created':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Time to offer':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Avg. revisions per offer':
        return [
          columnsMap.applicantName,
          columnsMap.offerRevision,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Offers accepted':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.associatedRequisition,
          columnsMap.offerCreatedBy,
          columnsMap.jobName,
          columnsMap.department,
          columnsMap.office,
        ];

      case 'Offer acceptance rate':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Offers rejected':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.associatedRequisition,
          columnsMap.offerCreatedBy,
          columnsMap.jobName,
          columnsMap.department,
          columnsMap.office,
        ];

      case 'Offer trend':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Offers by department':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          columnsMap.associatedRequisition,
          columnsMap.offerCreatedBy,
          columnsMap.jobName,
          columnsMap.office,
        ];

      case 'Offers by location':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          columnsMap.associatedRequisition,
          columnsMap.offerCreatedBy,
          columnsMap.jobName,
          columnsMap.department,
        ];

      case 'Offers':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Avg. time to offer':
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      case 'Avg. time to close offer':
      case 'Time to close offer':
        return [
          columnsMap.applicantName,
          columnsMap.timeToCloseOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];

      default:
        return [
          columnsMap.applicantName,
          columnsMap.timeToOffer,
          columnsMap.offerStatus,
          ...commonRequisitionCreatedByJobDeptOffice,
        ];
    }
  };

  return { columnConfig: getColumnConfig() };
};
